import { FormEvent, useState } from "react";
import { ApiError } from "../../api/client";
import { cancelAppointment, deleteAppointment } from "../../api/appointments";
import {
  APPOINTMENT_CANCEL_REASONS,
  type AppointmentCancelReasonCode,
} from "../../constants/appointmentCancelReasons";
import "../lesson-reports/LessonReportModal.css";
import "./CancelAppointmentModal.css";

type Props = {
  appointmentId: string;
  studentName?: string;
  startsAt?: string;
  endsAt?: string;
  onClose: () => void;
  onCancelled?: () => void;
  onDeleted?: () => void;
};

export function CancelAppointmentModal({
  appointmentId,
  studentName,
  startsAt,
  endsAt,
  onClose,
  onCancelled,
  onDeleted,
}: Props) {
  const [reasonCode, setReasonCode] = useState<AppointmentCancelReasonCode>("hasta");
  const [reasonNote, setReasonNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (reasonCode === "diger" && !reasonNote.trim()) {
      setError("Diğer seçildiğinde açıklama Zorunludur.");
      return;
    }
    setBusy(true);
    try {
      await cancelAppointment(appointmentId, {
        reasonCode,
        reasonNote: reasonCode === "diger" ? reasonNote.trim() : undefined,
      });
      onCancelled?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Randevu İptal Edilemedi.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      "Randevuyu silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz. İlgili zaman dilimi tekrar rezervasyon için boşalır.",
    );
    if (!confirmed) return;

    setError(null);
    setDeleting(true);
    try {
      await deleteAppointment(appointmentId);
      onDeleted?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Randevu silinemedi.");
    } finally {
      setDeleting(false);
    }
  };

  const isBusy = busy || deleting;

  const timeLabel =
    startsAt && endsAt
      ? `${new Date(startsAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })} – ${new Date(endsAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}`
      : null;

  return (
    <div className="report-modal-backdrop" role="dialog" aria-modal="true">
      <form className="report-modal cancel-appt-modal" onSubmit={handleSubmit}>
        <div className="report-modal__header">
          <div>
            <h3>Dersi İptal Et</h3>
            <div className="report-modal__sub">
              {studentName && <span>{studentName}</span>}
              {timeLabel && <span> · {timeLabel}</span>}
            </div>
          </div>
          <button type="button" className="report-modal__close" onClick={onClose} aria-label="Kapat">
            ×
          </button>
        </div>

        <div className="report-modal__body cancel-appt-modal__body">
          <p className="cancel-appt-modal__hint">
            İptal edilen derse değerlendirme raporu girilmez. Kursiyer bu dersi almamış sayılır.
          </p>

          <label className="cancel-appt-modal__field">
            <span className="report-modal__label">İptal sebebi</span>
            <select
              className="kp-input"
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value as AppointmentCancelReasonCode)}
              required
            >
              {APPOINTMENT_CANCEL_REASONS.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>

          {reasonCode === "diger" && (
            <label className="cancel-appt-modal__field">
              <span className="report-modal__label">Açıklama</span>
              <textarea
                className="kp-input cancel-appt-modal__textarea"
                rows={3}
                value={reasonNote}
                onChange={(e) => setReasonNote(e.target.value)}
                placeholder="İptal sebebini yazın..."
                maxLength={500}
                required
              />
            </label>
          )}

          {error && <div className="cancel-appt-modal__error">{error}</div>}
        </div>

        <div className="cancel-appt-modal__footer">
          <button
            type="button"
            className="kp-btn cancel-appt-modal__delete-btn"
            onClick={handleDelete}
            disabled={isBusy}
          >
            {deleting ? "Siliniyor..." : "Randevuyu Sil"}
          </button>
          <div className="cancel-appt-modal__footer-actions">
            <button type="button" className="kp-btn kp-btn--ghost" onClick={onClose} disabled={isBusy}>
              Vazgeç
            </button>
            <button type="submit" className="kp-btn kp-btn--danger" disabled={isBusy}>
              {busy ? "İptal ediliyor..." : "Dersi İptal Et"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
